import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useCustomerList } from "../../hooks/useCustomerList";
import { useContactPersons } from "../../hooks/useContactPersons";
import "./projects.style.css";

function ProjectFormView({
    newProject,
    loading,
    error,
    handleInputChange,
    handleSubmit,
    onCancel
}) {
    const { t } = useTranslation();
    const { customers, loading: customersLoading, error: customersError } = useCustomerList();

    // Track selected customer to fetch their contact persons
    const [selectedCustomerId, setSelectedCustomerId] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Find customer based on selected client name
    useEffect(() => {
        if (newProject.client && customers) {
            const customer = customers.find(c => c.name === newProject.client);
            setSelectedCustomerId(customer?.id || null);
            setSelectedCustomer(customer || null);
        } else {
            setSelectedCustomerId(null);
            setSelectedCustomer(null);
        }
    }, [newProject.client, customers]);

    const { contactPersons } = useContactPersons(selectedCustomerId);

    // Auto-select contact person when customer changes (only for new projects)
    useEffect(() => {
        if (selectedCustomer && contactPersons && contactPersons.length > 0) {
            // Only auto-select if no contact person is currently selected
            if (!newProject.contact_person_id) {
                const primaryContact = contactPersons.find(cp => cp.is_primary);
                const selectedContact = primaryContact || contactPersons[0];

                // Trigger change event to update the form
                const event = {
                    target: {
                        name: 'contact_person_id',
                        value: selectedContact.id
                    }
                };
                handleInputChange(event);
            }
        }
        // Only run when customer changes or contact persons are first loaded
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCustomer?.id, contactPersons?.length]);

    return (
        <form className="project-form-modal" onSubmit={handleSubmit} autoComplete="off">
            {error && (
                <div className="form-error mb-3" role="alert">
                    {error}
                </div>
            )}

                <div className="form-grid form-grid-2">
                    <div className="form-group form-item-full">
                        <label htmlFor="project-name" className="form-label required">
                            {t('projects.projectName')}
                        </label>
                        <input
                            id="project-name"
                            type="text"
                            name="name"
                            className="form-input"
                            value={newProject.name}
                            onChange={handleInputChange}
                            placeholder={t('projects.projectNamePlaceholder')}
                            required
                            autoFocus
                            aria-required="true"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="project-client" className="form-label required">
                            {t('projects.client')}
                        </label>
                        <select
                            id="project-client"
                            name="client"
                            className="form-input"
                            value={newProject.client}
                            onChange={handleInputChange}
                            required
                            aria-required="true"
                        >
                            <option value="" disabled>
                                {customersLoading ? t('common.loading') : t('projects.selectClient')}
                            </option>
                            {customers && customers.map((customer) => (
                                <option key={customer.id} value={customer.name}>{customer.name}</option>
                            ))}
                        </select>
                        {customersError && <span className="form-error" role="alert">{customersError}</span>}
                    </div>

                    {selectedCustomerId && (
                        <div className="form-group">
                            <label htmlFor="project-contact-person" className="form-label">
                                {t('customers.contactPerson')}
                            </label>

                            {contactPersons && contactPersons.length > 0 ? (
                                <select
                                    id="project-contact-person"
                                    name="contact_person_id"
                                    className="form-input"
                                    value={newProject.contact_person_id || ''}
                                    onChange={handleInputChange}
                                >
                                    <option value="">{t('customers.noContactPersonSelected')}</option>
                                    {contactPersons.map((cp) => (
                                        <option key={cp.id} value={cp.id}>
                                            {cp.name} {cp.is_primary ? '(Primary)' : ''} {cp.position ? `- ${cp.position}` : ''}
                                        </option>
                                    ))}
                                </select>
                            ) : selectedCustomer?.contact_person ? (
                                <div>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={selectedCustomer.contact_person}
                                        disabled
                                    />
                                    <span className="form-hint">
                                        {t('customers.mainContactPersonLegacy')}
                                    </span>
                                </div>
                            ) : (
                                <input
                                    type="text"
                                    className="form-input"
                                    value={t('customers.noContactPersons')}
                                    disabled
                                />
                            )}
                        </div>
                    )}

                    <div className="form-group">
                        <label htmlFor="project-location" className="form-label required">
                            {t('projects.city')}
                        </label>
                        <input
                            id="project-location"
                            type="text"
                            name="location"
                            className="form-input"
                            value={newProject.location}
                            onChange={handleInputChange}
                            placeholder={t('projects.cityPlaceholder')}
                            required
                            aria-required="true"
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="project-address" className="form-label required">
                            {t('projects.address')}
                        </label>
                        <input
                            id="project-address"
                            type="text"
                            name="address"
                            className="form-input"
                            value={newProject.address}
                            onChange={handleInputChange}
                            placeholder={t('projects.addressPlaceholder')}
                            required
                            aria-required="true"
                        />
                    </div>
                </div>

                <div className="form-section mt-3">
                    <h3 className="form-section-title text-base">
                        {t('projects.pricing')}
                    </h3>
                    <div className="form-grid form-grid-2">
                        <div className="form-group">
                            <label htmlFor="projectprice" className="form-label">
                                {t('projects.quotationPrice')}
                            </label>
                            <input
                                id="projectprice"
                                type="number"
                                name="projectprice"
                                className="form-input"
                                value={newProject.projectprice}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="projectrate" className="form-label">
                                {t('projects.hourlyRate')}
                            </label>
                            <input
                                id="projectrate"
                                type="number"
                                name="projectrate"
                                className="form-input"
                                value={newProject.projectrate}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="materialcosts" className="form-label">
                                {t('projects.materialCosts')}
                            </label>
                            <input
                                id="materialcosts"
                                type="number"
                                name="materialcosts"
                                className="form-input"
                                value={newProject.materialcosts}
                                onChange={handleInputChange}
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="whatsapp" className="form-label">
                                WhatsApp
                            </label>
                            <input
                                id="whatsapp"
                                type="text"
                                name="whatsapp"
                                className="form-input"
                                value={newProject.whatsapp || ''}
                                onChange={handleInputChange}
                                placeholder={t('projects.whatsappPlaceholder')}
                            />
                        </div>
                    </div>
                </div>

                <div className="form-section mt-3">
                    <h3 className="form-section-title text-base">
                        {t('projects.planning')}
                    </h3>
                    <div className="form-grid form-grid-2">
                        <div className="form-group">
                            <label htmlFor="start_date" className="form-label">
                                {t('projects.startDate')}
                            </label>
                            <input
                                id="start_date"
                                type="date"
                                name="start_date"
                                className="form-input"
                                value={newProject.start_date}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="end_date" className="form-label">
                                {t('projects.endDate')}
                            </label>
                            <input
                                id="end_date"
                                type="date"
                                name="end_date"
                                className="form-input"
                                value={newProject.end_date}
                                onChange={handleInputChange}
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="project-status" className="form-label">
                                {t('projects.status')}
                            </label>
                            <select
                                id="project-status"
                                name="status"
                                className="form-input"
                                value={newProject.status || 'active'}
                                onChange={handleInputChange}
                            >
                                <option value="active">{t('projects.active')}</option>
                                <option value="archived">{t('projects.archived')}</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={onCancel}
                    >
                        {t('common.cancel')}
                    </button>
                    <button
                        type="submit"
                        className="btn btn-submit"
                        disabled={loading}
                    >
                        {loading ? t('projects.creating') : t('projects.createProject')}
                    </button>
                </div>
        </form>
    );
}

ProjectFormView.propTypes = {
    newProject: PropTypes.object.isRequired,
    loading: PropTypes.bool,
    error: PropTypes.string,
    handleInputChange: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
};

export default ProjectFormView;
